'use client';

import { useId } from 'react';
import { Minus, Plus } from 'lucide-react';
import { tw } from '@tw';
import { PRICE_MAX, PRICE_MIN } from '@peach/types/payments/price-limits.js';
import { BundleType, type BundleDraft } from '@/components/products/bundle-config';
import { shownDealPrice } from '@/components/products/pricing';
import { CALLS_LIMITS, lengthLimits, priceStep } from '@/components/products/terms-fields';
import { useBundlesStore } from '@/components/products/use-bundles-store';
import { useNumberDraft } from '@/components/products/use-number-draft';
import { dealNote } from '@/components/products/price-copy';

/**
 * A product's settings under its card: how many calls, how long, its price
 * (what the owner gets, and nothing else moves it), and whether it shows an old
 * price above it. The title is typed into on the card.
 */
export function ProductEditor({ bundle }: { bundle: BundleDraft }) {
  const setTerms = useBundlesStore((s) => s.setTerms);
  const remove = useBundlesStore((s) => s.remove);
  const saved = useBundlesStore((s) => s.before?.find((b) => b.id === bundle.id));
  const isTrial = bundle.type === BundleType.TRIAL;
  const currency = useBundlesStore((s) => s.currency);
  const deal = dealNote(bundle.deal, shownDealPrice(bundle, saved), currency);

  return (
    <div className="flex flex-col gap-4 pt-1">
      {!isTrial && (
        <NumberField
          label="Calls"
          unit="calls"
          value={bundle.numberOfClasses}
          min={CALLS_LIMITS.min}
          max={CALLS_LIMITS.max}
          step={1}
          onCommit={(numberOfClasses) => setTerms(bundle.id, { numberOfClasses })}
        />
      )}
      <NumberField
        label="Length"
        unit="min"
        value={bundle.duration}
        {...lengthLimits(bundle.type)}
        step={1}
        onCommit={(duration) => setTerms(bundle.id, { duration })}
      />

      <NumberField
        label="Price"
        prefix="$"
        value={bundle.price}
        min={PRICE_MIN}
        max={PRICE_MAX}
        step={priceStep(bundle.type)}
        onCommit={(price) => setTerms(bundle.id, { price })}
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-p-3 font-semibold text-[var(--sf-ink)]">Make it a deal</span>
          <span className="text-badge text-[var(--sf-mut)]">{deal}</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={bundle.deal}
          aria-label="Make it a deal"
          onClick={() => setTerms(bundle.id, { deal: !bundle.deal })}
          className={tw(
            'relative h-8 w-14 shrink-0 rounded-full transition-colors',
            bundle.deal ? 'bg-[var(--sf-ink)]' : 'bg-[var(--sf-line)]'
          )}
        >
          <span
            className={tw(
              'absolute left-1 top-1 size-6 rounded-full bg-[var(--sf-card)] transition-transform',
              bundle.deal && 'translate-x-6'
            )}
          />
        </button>
      </div>

      {/* Not on the intro: a page keeps its intro call. */}
      {!isTrial && (
        <button
          type="button"
          onClick={() => remove(bundle.id)}
          className="mm-btn min-h-11 self-start text-p-3 font-semibold text-[var(--sf-mut)] underline underline-offset-4"
        >
          Remove from my page
        </button>
      )}
    </div>
  );
}

/** A number the owner types or steps (`useNumberDraft`), in the storefront card's style. */
function NumberField({
  label,
  unit,
  prefix,
  value,
  min,
  max,
  step,
  onCommit,
}: {
  label: string;
  unit?: string;
  prefix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (next: number) => void;
}) {
  const id = useId();
  const field = useNumberDraft({ value, min, max, step, onCommit });

  return (
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={id} className="text-p-3 font-semibold text-[var(--sf-mut)]">
        {label}
      </label>
      <div className="flex items-center gap-1.5">
        <StepButton label={`${label}: less`} disabled={field.atMin} onClick={field.less}>
          <Minus className="size-4" />
        </StepButton>
        <span className="flex min-w-24 items-baseline justify-center gap-1 rounded-xl bg-[var(--sf-page)] px-2.5 py-1.5">
          {prefix && <span className="text-p-3 font-semibold text-[var(--sf-mut)]">{prefix}</span>}
          <input
            id={id}
            inputMode="numeric"
            value={field.text}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            onKeyDown={field.onKeyDown}
            size={Math.max(field.text.length, 2)}
            className="sf-num bg-transparent text-right text-head-2 font-extrabold text-[var(--sf-ink)] outline-none"
          />
          {unit && <span className="text-p-3 font-semibold text-[var(--sf-mut)]">{unit}</span>}
        </span>
        <StepButton label={`${label}: more`} disabled={field.atMax} onClick={field.more} ink>
          <Plus className="size-4" />
        </StepButton>
      </div>
    </div>
  );
}

function StepButton({
  label,
  disabled,
  ink = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  ink?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={tw(
        'mm-btn flex size-11 items-center justify-center rounded-full disabled:opacity-35',
        ink
          ? 'bg-[var(--sf-ink)] text-[var(--sf-page)]'
          : 'bg-[var(--sf-page)] text-[var(--sf-ink)]'
      )}
    >
      {children}
    </button>
  );
}
